import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, ShoppingCart, User, Menu, X, ChevronDown, Phone, Facebook, Instagram, Moon, Sun, Home, PackageSearch, Mail, LayoutGrid, Percent } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import SearchAutocomplete from './SearchAutocomplete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [expandedCat, setExpandedCat] = useState(null);
    const [catsExpanded, setCatsExpanded] = useState(false);
    const { user, logout, isAdmin } = useAuth();
    const { itemCount } = useCart();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    // Load categories once for the mobile menu
    useEffect(() => {
        if (!mobileMenuOpen || categories.length > 0) return;
        axios.get(`${API}/categories`)
            .then(res => setCategories((res.data || []).filter(c => !c.is_draft && c.name)))
            .catch(() => {});
    }, [mobileMenuOpen, categories.length]);

    // Lock body scroll while the drawer is open
    useEffect(() => {
        document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    // Close drawer on navigation
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.search]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
            setMobileMenuOpen(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const navLinks = [
        { name: 'Начало', path: '/', icon: Home },
        { name: 'Продукти', path: '/products', icon: LayoutGrid },
        { name: 'Промоции', path: '/promotions', icon: Percent, highlight: true },
        { name: 'Проследи поръчка', path: '/track', icon: PackageSearch },
        { name: 'Контакти', path: '/contact', icon: Mail },
    ];

    const closeMenu = () => setMobileMenuOpen(false);

    return (
        <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-amber-100 dark:border-slate-700">
            {/* Top Bar - Warm Amber gradient with Social Links */}
            <div className="bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 dark:from-amber-800 dark:via-orange-700 dark:to-amber-800 text-white py-2">
                <div className="container-main">
                    <div className="flex items-center justify-between text-sm gap-2">
                        {/* Social Media Links - Left */}
                        <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-amber-100 font-medium hidden sm:inline">Последвайте ни:</span>
                            <a 
                                href="https://www.facebook.com/profile.php?id=61588073983274" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-7 h-7 rounded-full flex items-center justify-center bg-white/20 hover:bg-white/40 transition-colors"
                                aria-label="Facebook"
                            >
                                <Facebook className="h-3.5 w-3.5" />
                            </a>
                            <a 
                                href="https://www.instagram.com/vkusatnaturciq/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-7 h-7 rounded-full flex items-center justify-center bg-white/20 hover:bg-white/40 transition-colors"
                                aria-label="Instagram"
                            >
                                <Instagram className="h-3.5 w-3.5" />
                            </a>
                            <a 
                                href="https://www.tiktok.com/@diyanova?_r=1&_t=ZN-94nJYfaUqs2" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-7 h-7 rounded-full flex items-center justify-center bg-white/20 hover:bg-white/40 transition-colors"
                                aria-label="TikTok"
                            >
                                <TikTokIcon className="h-3.5 w-3.5" />
                            </a>
                        </div>
                        
                        {/* Center - Free Delivery */}
                        <p className="text-amber-100 font-medium hidden md:block">
                            🚚 Безплатна доставка в гр. София с наш шофьор
                        </p>
                        
                        {/* Phone - Right */}
                        <a href="tel:0899972727" className="flex items-center gap-1.5 text-white hover:text-amber-200 transition-colors font-semibold whitespace-nowrap">
                            <Phone className="h-4 w-4 flex-shrink-0" />
                            0899 97 27 27
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <div className="container-main py-3 lg:py-5">
                <div className="flex items-center justify-between gap-2 lg:gap-8">
                    {/* Logo */}
                    <Link to="/" data-testid="logo-link" className="flex-shrink min-w-0">
                        <h1 className="text-lg sm:text-2xl md:text-3xl font-bold tracking-tight whitespace-nowrap">
                            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Вкусът на</span>{' '}
                            <span className="text-slate-800 dark:text-white">Турция</span>
                        </h1>
                    </Link>

                    {/* Search - Desktop */}
                    <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-lg">
                        <div className="relative w-full">
                            <Input
                                data-testid="search-input"
                                type="text"
                                placeholder="Търсене на продукти..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pr-12 h-12 border-amber-200 dark:border-amber-800 dark:bg-slate-800 dark:text-white focus:border-amber-400 focus:ring-amber-400 rounded-xl"
                            />
                            <Button 
                                type="submit" 
                                size="icon" 
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                                data-testid="search-btn"
                            >
                                <Search className="h-4 w-4 text-white" />
                            </Button>
                        </div>
                    </form>

                    {/* Actions */}
                    <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
                        {/* Dark Mode Toggle - desktop only (mobile: inside drawer) */}
                        <button
                            onClick={toggleTheme}
                            className="hidden lg:block p-2.5 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors"
                            data-testid="theme-toggle"
                        >
                            {isDark ? (
                                <Sun className="h-5 w-5 text-amber-500" />
                            ) : (
                                <Moon className="h-5 w-5 text-slate-600" />
                            )}
                        </button>

                        {/* Search Modal Trigger - Mobile */}
                        <button
                            onClick={() => setShowSearchModal(true)}
                            className="lg:hidden p-2 sm:p-2.5 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors"
                            data-testid="mobile-search-btn"
                            aria-label="Търсене"
                        >
                            <Search className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                        </button>

                        {/* User Menu - desktop only (mobile: inside drawer) */}
                        <div className="hidden lg:block">
                            {user ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" data-testid="user-menu-btn" className="flex items-center gap-2 hover:bg-amber-50 px-3">
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold bg-gradient-to-br from-amber-500 to-orange-500">
                                                {user.name?.charAt(0) || 'U'}
                                            </div>
                                            <span className="hidden md:inline text-slate-700 font-medium">{user.name}</span>
                                            <ChevronDown className="h-4 w-4 text-slate-400" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-52">
                                        <DropdownMenuItem asChild>
                                            <Link to="/profile" data-testid="profile-link" className="font-medium cursor-pointer">
                                                Моят профил
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/orders" data-testid="orders-link" className="font-medium cursor-pointer">
                                                Моите поръчки
                                            </Link>
                                        </DropdownMenuItem>
                                        {isAdmin && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem asChild>
                                                    <Link to="/admin" data-testid="admin-link" className="font-medium text-amber-600 cursor-pointer">
                                                        Администрация
                                                    </Link>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="font-medium cursor-pointer">
                                            Изход
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Link to="/login">
                                    <Button variant="ghost" data-testid="login-link" className="flex items-center gap-2 hover:bg-amber-50 text-slate-700 px-4">
                                        <User className="h-5 w-5" />
                                        <span className="hidden md:inline font-medium">Вход</span>
                                    </Button>
                                </Link>
                            )}
                        </div>

                        {/* Cart */}
                        <Link to="/cart" data-testid="cart-link">
                            <Button className="relative px-3 sm:px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg">
                                <ShoppingCart className="h-5 w-5 text-white" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow">
                                        {itemCount}
                                    </span>
                                )}
                            </Button>
                        </Link>

                        {/* Mobile Menu Toggle */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="lg:hidden hover:bg-amber-50 dark:hover:bg-slate-700 h-10 w-10"
                            onClick={() => setMobileMenuOpen(true)}
                            data-testid="mobile-menu-btn"
                            aria-label="Меню"
                        >
                            <Menu className="h-6 w-6 text-slate-700 dark:text-slate-300" />
                        </Button>
                    </div>
                </div>

                {/* Navigation - Desktop */}
                <nav className="hidden lg:flex items-center gap-10 mt-5 pt-5 border-t border-amber-100">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                            className="text-slate-600 hover:text-amber-600 font-medium transition-colors text-[15px]"
                        >
                            {link.name}
                        </Link>
                    ))}
                </nav>
            </div>

            {/* Mobile Menu Drawer */}
            {mobileMenuOpen && createPortal(
                <div className="lg:hidden fixed inset-0 z-[90]">
                    {/* Overlay */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={closeMenu}
                        data-testid="mobile-menu-overlay"
                    />
                    {/* Panel */}
                    <div className="absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-slide-in-right" data-testid="mobile-menu-drawer">
                        {/* Drawer header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-amber-100 dark:border-slate-700 flex-shrink-0">
                            <span className="font-bold text-lg text-slate-800 dark:text-white">Меню</span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={toggleTheme}
                                    className="p-2.5 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors"
                                    data-testid="mobile-theme-toggle"
                                    aria-label="Смени темата"
                                >
                                    {isDark ? <Sun className="h-5 w-5 text-amber-500" /> : <Moon className="h-5 w-5 text-slate-600" />}
                                </button>
                                <button
                                    onClick={closeMenu}
                                    className="p-2.5 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors"
                                    data-testid="mobile-menu-close"
                                    aria-label="Затвори менюто"
                                >
                                    <X className="h-6 w-6 text-slate-700 dark:text-slate-300" />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
                            {/* Search */}
                            <form onSubmit={handleSearch} className="mb-4">
                                <div className="relative">
                                    <Input
                                        type="text"
                                        placeholder="Търсене..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pr-12 h-12 border-amber-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-xl"
                                        data-testid="mobile-menu-search-input"
                                    />
                                    <Button 
                                        type="submit" 
                                        size="icon" 
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500"
                                    >
                                        <Search className="h-4 w-4 text-white" />
                                    </Button>
                                </div>
                            </form>

                            {/* Nav links */}
                            <nav className="flex flex-col gap-0.5">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        onClick={closeMenu}
                                        className="flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 hover:text-amber-600 font-medium transition-colors rounded-xl"
                                        data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                                    >
                                        <link.icon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                                        {link.name}
                                    </Link>
                                ))}
                            </nav>

                            {/* Categories accordion */}
                            <div className="mt-3 pt-3 border-t border-amber-100 dark:border-slate-700">
                                <button
                                    onClick={() => setCatsExpanded(!catsExpanded)}
                                    className="w-full flex items-center justify-between py-3 px-3 rounded-xl font-semibold text-amber-800 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition-colors"
                                    data-testid="mobile-categories-toggle"
                                >
                                    <span className="flex items-center gap-3">
                                        <LayoutGrid className="h-5 w-5 flex-shrink-0" />
                                        Категории
                                    </span>
                                    <ChevronDown className={`h-5 w-5 transition-transform ${catsExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {catsExpanded && (
                                    <div className="mt-1 space-y-0.5">
                                        {categories.length === 0 && (
                                            <p className="text-sm text-slate-400 px-3 py-2">Зареждане...</p>
                                        )}
                                        {categories.map((cat) => {
                                            const hasSubs = (cat.subcategories || []).length > 0;
                                            const isOpen = expandedCat === cat.category_id;
                                            return (
                                                <div key={cat.category_id}>
                                                    <div className="flex items-center">
                                                        <Link
                                                            to={`/products?category=${cat.category_id}`}
                                                            onClick={closeMenu}
                                                            className="flex-1 min-w-0 py-2.5 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 hover:text-amber-600 rounded-xl transition-colors truncate"
                                                            data-testid={`mobile-cat-link-${cat.category_id}`}
                                                        >
                                                            {cat.name}
                                                        </Link>
                                                        {hasSubs && (
                                                            <button
                                                                onClick={() => setExpandedCat(isOpen ? null : cat.category_id)}
                                                                className="p-2.5 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-800 flex-shrink-0"
                                                                data-testid={`mobile-cat-toggle-${cat.category_id}`}
                                                                aria-label={`Подкатегории на ${cat.name}`}
                                                            >
                                                                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {hasSubs && isOpen && (
                                                        <div className="ml-4 pl-3 border-l-2 border-amber-200 dark:border-amber-700 space-y-0.5 mb-1">
                                                            {cat.subcategories.map((sub) => (
                                                                <Link
                                                                    key={sub}
                                                                    to={`/products?category=${cat.category_id}&subcategory=${encodeURIComponent(sub)}`}
                                                                    onClick={closeMenu}
                                                                    className="block py-2 px-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-amber-50 dark:hover:bg-slate-800 hover:text-amber-600 rounded-lg transition-colors truncate"
                                                                >
                                                                    {sub}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Account */}
                            <div className="mt-3 pt-3 border-t border-amber-100 dark:border-slate-700 space-y-0.5">
                                {user ? (
                                    <>
                                        <Link to="/profile" onClick={closeMenu} className="flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl" data-testid="mobile-profile-link">
                                            <User className="h-5 w-5 text-amber-500" /> Моят профил
                                        </Link>
                                        <Link to="/orders" onClick={closeMenu} className="flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl">
                                            <PackageSearch className="h-5 w-5 text-amber-500" /> Моите поръчки
                                        </Link>
                                        {isAdmin && (
                                            <Link to="/admin" onClick={closeMenu} className="flex items-center gap-3 py-3 px-3 text-amber-600 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl">
                                                <LayoutGrid className="h-5 w-5" /> Администрация
                                            </Link>
                                        )}
                                        <button onClick={() => { handleLogout(); closeMenu(); }} className="w-full flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl text-left" data-testid="mobile-logout-btn">
                                            <X className="h-5 w-5 text-red-500" /> Изход
                                        </button>
                                    </>
                                ) : (
                                    <Link to="/login" onClick={closeMenu} className="flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl" data-testid="mobile-login-link">
                                        <User className="h-5 w-5 text-amber-500" /> Вход / Регистрация
                                    </Link>
                                )}
                                <Link to="/cart" onClick={closeMenu} className="flex items-center gap-3 py-3 px-3 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 font-medium rounded-xl" data-testid="mobile-cart-link">
                                    <ShoppingCart className="h-5 w-5 text-amber-500" />
                                    Количка
                                    {itemCount > 0 && (
                                        <span className="ml-auto w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500">
                                            {itemCount}
                                        </span>
                                    )}
                                </Link>
                            </div>

                            {/* Phone */}
                            <a href="tel:0899972727" className="mt-4 mb-2 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow">
                                <Phone className="h-5 w-5" /> 0899 97 27 27
                            </a>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Search Modal */}
            {showSearchModal && createPortal(
                <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-slate-800">Търсене</h3>
                            <button onClick={() => setShowSearchModal(false)} className="p-2 hover:bg-amber-50 rounded-full">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <SearchAutocomplete onClose={() => setShowSearchModal(false)} />
                    </div>
                </div>,
                document.body
            )}
        </header>
    );
};

export default Header;
